export const DEFAULT_TACO_PORTAL_LOGIN_URL =
  'https://portal.urtaco.com/login';
export const STANDALONE_LOGIN_PATH = '/app/login';
const CANONICAL_PORTAL_ORIGIN = 'https://portal.urtaco.com';

const MANAGED_SERVICE_CODES = new Set([
  'ai_bot',
  'meta_channels',
  'salla',
  'foodics',
  'easy_orders',
  'daftara',
  'whatsapp_free',
  'custom_domain',
  'custom_branding',
]);

const MANAGED_INTEGRATION_CODES = new Set([
  'salla',
  'foodics',
  'easy_orders',
  'daftara',
]);

export const unavailablePlatformPolicy = () => ({
  policyAvailable: false,
  services: {},
  renewal: {},
});

export const normalizeAccountId = accountId => {
  const normalized = String(accountId || '');
  return /^[1-9]\d*$/.test(normalized) ? normalized : '';
};

export const isStandaloneDeployment = chatwootConfig =>
  chatwootConfig?.tacoDeploymentMode === 'standalone';

export const isValidSSOCredentials = (email, token) =>
  typeof email === 'string' &&
  email.trim().length > 0 &&
  email.length <= 254 &&
  typeof token === 'string' &&
  /^[a-f0-9]{64}$/.test(token);

export const normalizePlatformPolicy = payload => {
  if (payload?.policy_available !== true) return unavailablePlatformPolicy();

  const services =
    payload.services && typeof payload.services === 'object'
      ? payload.services
      : {};
  const renewal =
    payload.renewal && typeof payload.renewal === 'object'
      ? payload.renewal
      : {};

  return { ...payload, policyAvailable: true, services, renewal };
};

export const isPlatformServiceEnabled = (policy, serviceCode) => {
  if (policy?.policyAvailable !== true) return false;

  const service = policy.services?.[serviceCode];
  return service?.enabled === true && service.status === 'active';
};

export const isManagedPlatformIntegration = integrationCode =>
  MANAGED_INTEGRATION_CODES.has(integrationCode);

export const isWhatsappFreeInbox = inbox => {
  if (inbox?.channel_type !== 'Channel::Api') return false;

  const attributes = inbox.additional_attributes || {};
  return (
    attributes.provider === 'whatsapp_qr' ||
    attributes.mode === 'managed' ||
    Boolean(attributes.whatsapp_qr)
  );
};

export const isMetaInbox = inbox =>
  ['Channel::FacebookPage', 'Channel::Instagram'].includes(
    inbox?.channel_type
  );

export const platformServiceForRoute = route => {
  const declaredService = route.meta?.platformService;
  if (MANAGED_SERVICE_CODES.has(declaredService)) return declaredService;
  if (route.params?.sub_page === 'whatsapp_free') return 'whatsapp_free';

  const integrationCode = route.params?.integration_id;
  return isManagedPlatformIntegration(integrationCode) ? integrationCode : '';
};

export const safeHttpURL = value => {
  if (
    typeof value !== 'string' ||
    !/^https:\/\/portal\.urtaco\.com(?:[/?#]|$)/i.test(value) ||
    !URL.canParse(value)
  )
    return '';

  const parsedURL = new URL(value);
  if (
    parsedURL.protocol !== 'https:' ||
    parsedURL.hostname !== 'portal.urtaco.com' ||
    parsedURL.port ||
    parsedURL.username ||
    parsedURL.password ||
    parsedURL.hash ||
    parsedURL.pathname.startsWith('//') ||
    parsedURL.pathname.includes('\\')
  )
    return '';

  return `${CANONICAL_PORTAL_ORIGIN}${parsedURL.pathname || '/'}${parsedURL.search}`;
};

export const portalLoginURL = globalConfig => {
  const configuredURL =
    globalConfig?.TACO_PORTAL_LOGIN_URL ||
    globalConfig?.LOGOUT_REDIRECT_LINK;
  return safeHttpURL(configuredURL) || DEFAULT_TACO_PORTAL_LOGIN_URL;
};

export const authenticationRedirectURL = (globalConfig, chatwootConfig) =>
  isStandaloneDeployment(chatwootConfig)
    ? STANDALONE_LOGIN_PATH
    : portalLoginURL(globalConfig);

export const safeRenewalCheckoutURL = value => {
  if (typeof value !== 'string' || !URL.canParse(value)) return '';

  const parsedURL = new URL(value);
  if (
    parsedURL.protocol !== 'https:' ||
    !parsedURL.hostname ||
    parsedURL.username ||
    parsedURL.password ||
    parsedURL.hash
  )
    return '';

  return parsedURL.toString();
};

export const isValidSSOLoginRoute = route => {
  const email = route.query?.email;
  const token = route.query?.sso_auth_token;
  return route.name === 'login' && isValidSSOCredentials(email, token);
};
